import { canonicalizeClientCode } from '../session/client-code-canonicalizer';
import type { AuthConfig } from '../config/app-config.model';
import {
  CLIENT_ROLES,
  INTERNAL_ROLES,
  type AppRole,
  type Session,
} from '../session/session.model';

/**
 * Per-issuer claim mapping (R-104, contracts/session-derivation.contract.md):
 * claim names are carried by the runtime configuration, never hard-coded, so
 * an IdP convention change is a configuration edit. Claims are read from the
 * validated ID token of the active configuration only — the library validates
 * signature, issuer, audience, nonce, and expiry before exposure, and no
 * storage, URL, user input, or non-validated source ever influences the
 * outcome (FR-004).
 */

/** The named OIDC configuration that produced the authentication result — never a claim value. */
export type IssuerCategory = 'staff' | 'client';

/** The named OIDC configuration ids — the issuer category source (R-102, C1). */
export const STAFF_CONFIG_ID: IssuerCategory = 'staff';
export const CLIENT_CONFIG_ID: IssuerCategory = 'client';

export interface ClaimNames {
  readonly rolesClaim: string;
  readonly tenantClaim: string | null;
}

/** Validated ID-token claims of the active configuration. */
export interface ValidatedClaims {
  readonly configId: IssuerCategory;
  readonly claims: Record<string, unknown>;
}

/** Derivation output is total: exactly one of the two; the not-provisioned outcome carries NO claim data. */
export type SessionDerivation =
  | { readonly kind: 'session'; readonly session: Session }
  | { readonly kind: 'notProvisioned' };

/**
 * Rule 1: the roles claim may be a string or an array of strings; anything
 * else (absent, number, object) reads as the empty set. Non-string members of
 * a mixed array are dropped silently.
 */
export function readRoleValues(
  claims: Record<string, unknown>,
  rolesClaim: string,
): readonly string[] {
  const value = claims[rolesClaim];
  if (typeof value === 'string') return [value];
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string');
}

/**
 * Rule 2: issuer-scoped recognition (FR-004) — intersect with the category's
 * recognized set. Foreign-category and unrecognized values are dropped
 * silently; they can never add visibility.
 */
export function recognizedRolesFor(
  category: IssuerCategory,
  roleValues: readonly string[],
): AppRole[] {
  const recognized: readonly AppRole[] = category === 'staff' ? INTERNAL_ROLES : CLIENT_ROLES;
  const seen = new Set<string>();
  const roles: AppRole[] = [];
  for (const value of roleValues) {
    if (seen.has(value)) continue;
    seen.add(value);
    if (recognized.includes(value as AppRole)) roles.push(value as AppRole);
  }
  return roles;
}

/** Rule 4: the tenant claim must be a non-empty string; anything else reads as missing. */
export function readTenantValue(
  claims: Record<string, unknown>,
  tenantClaim: string,
): string | null {
  const value = claims[tenantClaim];
  if (typeof value !== 'string' || value.trim() === '') return null;
  return value;
}

/**
 * Reads display-only identity data from validated claims. Name is preferred;
 * `preferred_username` is a useful Entra fallback. Values stay memory-only.
 */
export function readDisplayName(claims: Record<string, unknown>): string | null {
  for (const key of ['name', 'preferred_username']) {
    const value = claims[key];
    if (typeof value === 'string' && value.trim() !== '') return value.trim().slice(0, 120);
  }
  return null;
}

/** Resolves the configured claim names for an issuer category; null when the issuer block is absent. */
export function issuerClaimNames(
  auth: AuthConfig | null,
  category: IssuerCategory,
): ClaimNames | null {
  if (auth === null) return null;
  const issuer = category === 'staff' ? auth.staff : auth.client;
  if (issuer === null) return null;
  return {
    rolesClaim: issuer.rolesClaim,
    tenantClaim: category === 'staff' ? null : auth.client?.tenantClaim ?? null,
  };
}

/**
 * The total claims → Session derivation (rules 1–7). Fail-closed: an empty
 * surviving role set, or a client session without a canonically valid tenant
 * code, is `notProvisioned` and carries no claim data (FR-012).
 */
export function deriveSession(
  input: ValidatedClaims,
  names: ClaimNames,
): SessionDerivation {
  const roles = recognizedRolesFor(
    input.configId,
    readRoleValues(input.claims, names.rolesClaim),
  );
  if (roles.length === 0) return { kind: 'notProvisioned' };

  if (input.configId === 'staff') {
    // Rule 5: any tenant claim present is ignored entirely.
    return {
      kind: 'session',
      session: sessionWithDisplayName({ isAuthenticated: true, roles, clientCode: null }, input.claims),
    };
  }

  // Rule 4: the tenant value passes through the client-code-canonicalizer
  // boundary and is used verbatim (exact, case-sensitive) thereafter.
  if (names.tenantClaim === null) return { kind: 'notProvisioned' };
  const tenantValue = readTenantValue(input.claims, names.tenantClaim);
  const clientCode = tenantValue === null ? null : canonicalizeClientCode(tenantValue);
  if (clientCode === null) return { kind: 'notProvisioned' };

  return {
    kind: 'session',
    session: sessionWithDisplayName({ isAuthenticated: true, roles, clientCode }, input.claims),
  };
}

function sessionWithDisplayName(session: Session, claims: Record<string, unknown>): Session {
  const displayName = readDisplayName(claims);
  return displayName === null ? session : { ...session, displayName };
}
